import type { CostDraft, MatchedDraft, UnmatchedCell } from './costDraft';

export interface AliasRow { ref: string; componentSku: string; }

/** trim, collapse internal whitespace, lowercase. Deliberately nothing more:
 *  stripping punctuation would collapse genuinely different part numbers. */
export function normalizeRef(ref: string): string {
  return ref.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Levenshtein distance, bounded — only used to offer a suggestion. */
function distance(a: string, b: string): number {
  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0]!;
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j]!;
      prev[j] = Math.min(prev[j]! + 1, prev[j - 1]! + 1,
                         diag + (a[i - 1] === b[j - 1] ? 0 : 1));
      diag = tmp;
    }
  }
  return prev[b.length]!;
}

export function matchDraft(
  draft: CostDraft, aliases: AliasRow[],
  componentSkus: string[], productSkus: string[],
): MatchedDraft {
  const byComponent = new Map(componentSkus.map((s) => [normalizeRef(s), s]));
  const byProduct = new Map(productSkus.map((s) => [normalizeRef(s), s]));
  const byAlias = new Map(aliases.map((a) => [normalizeRef(a.ref), a.componentSku]));

  const matched: MatchedDraft['matched'] = [];
  const unmatched: UnmatchedCell[] = [];

  for (const cell of draft.cells) {
    const key = normalizeRef(cell.ref);
    const component = byComponent.get(key);
    const product = byProduct.get(key);

    // Classification is by CATALOG MEMBERSHIP, never by prefix. VCH-FRU-* is a
    // component wearing the product prefix; reading it as a product would treat
    // a part cost as a customer price.
    if (component && product) {
      unmatched.push({ cell, reason: 'ambiguous' });   // the number has two meanings
      continue;
    }
    if (component) {
      matched.push({ ...cell, target: { type: 'component', componentSku: component } });
      continue;
    }
    if (product) {
      matched.push({ ...cell, target: { type: 'product', productSku: product } });
      continue;
    }
    // Aliases only ever point at components — a product price is always written
    // under its real SKU, because the review has to name what the PM typed.
    const aliased = byAlias.get(key);
    if (aliased) {
      matched.push({ ...cell, target: { type: 'component', componentSku: aliased } });
      continue;
    }

    let suggestion: string | undefined;
    let best = Infinity;
    for (const [normalized, real] of [...byComponent, ...byProduct]) {
      const d = distance(key, normalized);
      if (d < best) { best = d; suggestion = real; }
    }
    unmatched.push(best <= 3 && suggestion
      ? { cell, reason: 'near-miss', suggestion }
      : { cell, reason: 'unknown' });
  }

  // Decision 3: refuse a draft that writes the same target twice in one quarter.
  // AFTER matching, not on raw refs — 'CMP-DRAM-64G' and 'cmp-dram-64g' are one
  // target, and an alias can collapse two different spellings onto one component.
  // Throwing rather than returning a Blocked row is deliberate: this is a
  // malformed DRAFT, not a cell that failed to resolve, and nothing should be
  // staged from it. detectStaleness cannot catch it later (it compares against
  // the DB, not the draft) and the deterministic id would collide mid-commit,
  // after the PM already approved.
  const seen = new Set<string>();
  for (const cell of matched) {
    const t = cell.target!;
    const sku = t.type === 'component' ? t.componentSku : t.productSku;
    const key = `${sku}@${cell.quarter}`;
    if (seen.has(key)) {
      throw new Error(
        `${sku} appears twice for ${cell.quarter} in ${draft.sourceLabel}. ` +
        `Remove the duplicate row and re-import.`);
    }
    seen.add(key);
  }

  return { vendor: draft.vendor, matched, unmatched };
}
